import { useSyncExternalStore } from 'react';
import { getNetworkStatus } from './networkStatus';

const listeners = new Set();
const activeAttempts = new Set();
const cachedSnapshots = new Map();
const memoryFallback = new Map();
const expiryTimers = new Map();

let storageListening = false;

export const UNKNOWN_MESSAGE =
    'Bet status is unknown. Check My Bets before submitting again.';

const STORAGE_PREFIX = 'unresolved_bet_action_';

const PENDING_STALE_MS = 45 * 1000;

/*
 * Safety fallback only.
 *
 * An UNKNOWN result should preferably be resolved by My Bets/status API.
 * This timeout prevents a dead browser tab from blocking the user forever.
 */
const UNKNOWN_EXPIRY_MS = 30 * 1000;

const DEFAULT_RESOLVE_INTERVAL_MS = 3000;
const DEFAULT_RESOLVE_ATTEMPTS = 10;

const storageKey = (userId) =>
    `${STORAGE_PREFIX}${String(userId)}`;

/* -------------------------------------------------------------------------- */
/* Basic helpers                                                              */
/* -------------------------------------------------------------------------- */

const notifyListeners = () => {
    listeners.forEach((listener) => {
        try {
            listener();
        } catch {
            // A subscriber must never break betting safety.
        }
    });
};

const clearExpiryTimer = (userId) => {
    const key = String(userId);
    const existing = expiryTimers.get(key);

    if (existing?.timer) {
        clearTimeout(existing.timer);
    }

    expiryTimers.delete(key);
};

const readStoredValue = (userId) => {
    if (!userId) return null;

    const key = storageKey(userId);

    try {
        if (typeof window === 'undefined') {
            return memoryFallback.get(key) ?? null;
        }

        return window.localStorage.getItem(key);
    } catch {
        return memoryFallback.get(key) ?? null;
    }
};

const writeStoredValue = (userId, value) => {
    if (!userId) return;

    const key = storageKey(userId);

    try {
        if (typeof window !== 'undefined') {
            if (value == null) {
                window.localStorage.removeItem(key);
            } else {
                window.localStorage.setItem(key, value);
            }
        } else {
            if (value == null) {
                memoryFallback.delete(key);
            } else {
                memoryFallback.set(key, value);
            }
        }
    } catch {
        if (value == null) {
            memoryFallback.delete(key);
        } else {
            memoryFallback.set(key, value);
        }
    }

    if (value == null) {
        clearExpiryTimer(userId);
    }

    cachedSnapshots.delete(String(userId));

    notifyListeners();
};

const parseJSON = (serialized) => {
    if (!serialized) return null;

    try {
        const parsed = JSON.parse(serialized);

        if (!parsed || typeof parsed !== 'object') {
            return null;
        }

        return parsed;
    } catch {
        return null;
    }
};

/* -------------------------------------------------------------------------- */
/* Expiry                                                                     */
/* -------------------------------------------------------------------------- */

const scheduleExpiry = (userId, expiresAt) => {
    if (!userId || !expiresAt) return;

    const key = String(userId);
    const existing = expiryTimers.get(key);

    if (existing?.at === expiresAt) {
        return;
    }

    clearExpiryTimer(userId);

    const delay =
        Math.max(0, expiresAt - Date.now()) + 50;

    const timer = setTimeout(() => {
        expiryTimers.delete(key);

        const current = parseSnapshot(userId);

        if (!current) {
            writeStoredValue(userId, null);
            return;
        }

        notifyListeners();
    }, delay);

    expiryTimers.set(key, {
        timer,
        at: expiresAt,
    });
};

/* -------------------------------------------------------------------------- */
/* Snapshot                                                                   */
/* -------------------------------------------------------------------------- */

const parseSnapshot = (userId) => {
    if (!userId) return null;

    const cacheKey = String(userId);
    const serialized = readStoredValue(userId);

    const cached = cachedSnapshots.get(cacheKey);

    if (
        cached &&
        cached.serialized === serialized &&
        !(
            cached.expiresAt &&
            Date.now() >= cached.expiresAt
        )
    ) {
        return cached.value;
    }

    let value = null;
    let expiresAt = null;

    const parsed = parseJSON(serialized);

    if (parsed) {
        const now = Date.now();

        /* ------------------------------ PENDING ----------------------------- */

        if (parsed.status === 'pending') {
            const startedAt =
                Number(parsed.startedAt || 0);

            /*
             * Request is genuinely running in this browser tab.
             */
            if (
                parsed.attemptId &&
                activeAttempts.has(parsed.attemptId)
            ) {
                value = parsed;
            } else {
                /*
                 * Request belonged to a tab/page which disappeared.
                 * Do not block forever.
                 */
                expiresAt =
                    startedAt +
                    PENDING_STALE_MS;

                if (now < expiresAt) {
                    value = {
                        ...parsed,
                        status: 'unknown',
                        unknownAt: now,
                        recoveredFromStalePending: true,
                        message: UNKNOWN_MESSAGE,
                    };
                } else {
                    value = null;
                }
            }
        }

        /* ------------------------------ UNKNOWN ----------------------------- */

        else if (parsed.status === 'unknown') {
            const unknownAt =
                Number(
                    parsed.unknownAt ||
                    parsed.startedAt ||
                    0
                );

            expiresAt =
                unknownAt +
                UNKNOWN_EXPIRY_MS;

            if (now < expiresAt) {
                value = parsed;
            } else {
                value = null;
            }
        }
    }

    cachedSnapshots.set(cacheKey, {
        serialized,
        value,
        expiresAt,
    });

    if (value && expiresAt) {
        scheduleExpiry(
            userId,
            expiresAt
        );
    }

    return value;
};

export const getBetActionSafety = (userId) => {
    return parseSnapshot(userId);
};

export const hasUnknownBetAction = (safety) =>
    safety?.status === 'unknown';

/* -------------------------------------------------------------------------- */
/* Clear / subscribe                                                          */
/* -------------------------------------------------------------------------- */

export const clearBetActionSafety = (userId) => {
    if (!userId) return;

    writeStoredValue(
        userId,
        null
    );
};

export const subscribeToBetActionSafety = (listener) => {
    listeners.add(listener);

    if (
        !storageListening &&
        typeof window !== 'undefined'
    ) {
        window.addEventListener(
            'storage',
            handleStorageChange
        );

        storageListening = true;
    }

    return () => {
        listeners.delete(listener);

        if (
            listeners.size === 0 &&
            storageListening &&
            typeof window !== 'undefined'
        ) {
            window.removeEventListener(
                'storage',
                handleStorageChange
            );

            storageListening = false;
        }
    };
};

function handleStorageChange(event) {
    if (
        !event.key ||
        !event.key.startsWith(STORAGE_PREFIX)
    ) {
        return;
    }

    const userId =
        event.key.slice(
            STORAGE_PREFIX.length
        );

    cachedSnapshots.delete(userId);

    notifyListeners();
}

export function useBetActionSafety(userId) {
    return useSyncExternalStore(
        subscribeToBetActionSafety,
        () => getBetActionSafety(userId),
        () => null
    );
}

/* -------------------------------------------------------------------------- */
/* Errors                                                                     */
/* -------------------------------------------------------------------------- */

const makeError = (
    message,
    status,
    extra = {}
) => {
    const error = new Error(message);

    error.betSafetyStatus = status;
    error.betStatusUnknown =
        status === 'unknown';

    error.response = {
        data: {
            message,
            status,
            ...extra,
        },
    };

    return error;
};

const isAmbiguousFailure = (
    error,
    ambiguousStatuses = []
) => {
    const responseStatus =
        Number(
            error?.response?.status
        );

    const dataStatus =
        error?.response?.data?.status;

    const errorCode =
        String(
            error?.code || ''
        ).toUpperCase();

    const message =
        String(
            error?.response?.data?.message ||
            error?.message ||
            ''
        ).toLowerCase();

    const ambiguousMessage =
        message.includes(
            'bet status is unknown'
        ) ||
        message.includes(
            'status is unknown'
        ) ||
        message.includes(
            'request failed'
        ) ||
        message.includes(
            'network error'
        ) ||
        message.includes(
            'timeout'
        ) ||
        message.includes(
            'timed out'
        );

    const ambiguousHttpStatus =
        responseStatus >= 500 ||
        ambiguousStatuses.includes(
            responseStatus
        );

    const ambiguousNetworkCode =
        [
            'ECONNABORTED',
            'ETIMEDOUT',
            'ERR_NETWORK',
            'ECONNRESET',
            'ENETUNREACH',
            'EAI_AGAIN',
        ].includes(errorCode);

    const noResponse =
        !error?.response &&
        (
            Boolean(error?.request) ||
            Boolean(error?.code) ||
            ambiguousMessage
        );

    return (
        dataStatus === 'unknown' ||
        error?.betStatusUnknown === true ||
        ambiguousHttpStatus ||
        ambiguousNetworkCode ||
        noResponse ||
        ambiguousMessage
    );
};

/* -------------------------------------------------------------------------- */
/* Attempt creation                                                           */
/* -------------------------------------------------------------------------- */

const createAttempt = (
    userId,
    details = {}
) => {
    const now = Date.now();

    return {
        attemptId:
            `${now}-${Math.random()
                .toString(36)
                .slice(2)}`,

        userId: String(userId),

        status: 'pending',

        startedAt: now,

        gameId:
            details?.gameId ??
            details?.sportId ??
            null,

        matchId:
            details?.matchId ??
            details?.roundId ??
            null,

        roundId:
            details?.roundId ??
            details?.matchId ??
            null,

        marketType:
            details?.marketType ??
            null,

        selection:
            details?.selection ??
            null,

        betType:
            details?.betType ??
            null,

        stake:
            details?.stake ??
            null,
    };
};

/* -------------------------------------------------------------------------- */
/* Main bet action                                                            */
/* -------------------------------------------------------------------------- */

export async function runBetAction(
    userId,
    details,
    request,
    options = {}
) {
    if (!userId) {
        return request();
    }

    if (
        typeof navigator !== 'undefined' &&
        navigator.locks?.request
    ) {
        return navigator.locks.request(
            `bet-action-${userId}`,
            {
                mode: 'exclusive',
                ifAvailable: true,
            },
            async (lock) => {
                if (!lock) {
                    throw makeError(
                        'A bet request is already in progress in another tab. Please wait.',
                        'pending'
                    );
                }

                return runBetActionWithLock(
                    userId,
                    details,
                    request,
                    options
                );
            }
        );
    }

    return runBetActionWithLock(
        userId,
        details,
        request,
        options
    );
}

async function runBetActionWithLock(
    userId,
    details,
    request,
    options = {}
) {
    /* ------------------------------ NETWORK ------------------------------- */

    const network =
        getNetworkStatus();

    if (network && network.browserOnline === false) {
        throw makeError(
            'Connection unavailable. Wait for the game to synchronize before submitting.',
            'unavailable'
        );
    }

    /* ------------------------------ EXISTING ------------------------------ */

    const existing =
        getBetActionSafety(userId);

    if (existing) {
        if (
            existing.status === 'unknown'
        ) {
            throw makeError(
                UNKNOWN_MESSAGE,
                'unknown',
                {
                    attemptId:
                        existing.attemptId,

                    matchId:
                        existing.matchId,

                    roundId:
                        existing.roundId,
                }
            );
        }

        throw makeError(
            'A bet request is already in progress. Please wait.',
            'pending',
            {
                attemptId:
                    existing.attemptId,
            }
        );
    }

    /* ------------------------------- CREATE ------------------------------- */

    const attempt =
        createAttempt(
            userId,
            details
        );

    activeAttempts.add(
        attempt.attemptId
    );

    writeStoredValue(
        userId,
        JSON.stringify(attempt)
    );

    try {
        const response =
            await request();

        activeAttempts.delete(
            attempt.attemptId
        );

        const current =
            getBetActionSafety(userId);

        /*
         * Only clear the same attempt.
         * Never clear another newer attempt.
         */
        if (
            current?.attemptId ===
            attempt.attemptId
        ) {
            writeStoredValue(
                userId,
                null
            );
        }

        return response;
    } catch (error) {
        activeAttempts.delete(
            attempt.attemptId
        );

        const ambiguous =
            isAmbiguousFailure(
                error,
                options.ambiguousStatuses || []
            );

        /* --------------------------- UNKNOWN ------------------------------ */

        if (ambiguous) {
            const failure = {
                code:
                    error?.code ??
                    null,

                httpStatus:
                    error?.response?.status ??
                    null,

                message:
                    String(
                        error?.response?.data?.message ||
                        error?.message ||
                        ''
                    ).slice(0, 200),
            };

            const unknown = {
                ...attempt,

                status: 'unknown',

                unknownAt: Date.now(),

                message:
                    UNKNOWN_MESSAGE,

                failure,
            };

            writeStoredValue(
                userId,
                JSON.stringify(unknown)
            );

            const safeError =
                new Error(
                    UNKNOWN_MESSAGE
                );

            safeError.name =
                'BetStatusUnknownError';

            safeError.code =
                error?.code ||
                'BET_STATUS_UNKNOWN';

            safeError.betStatusUnknown =
                true;

            safeError.betSafetyStatus =
                'unknown';

            safeError.attemptId =
                attempt.attemptId;

            safeError.response = {
                ...(error?.response || {}),

                data: {
                    ...(error?.response?.data &&
                    typeof error.response.data === 'object'
                        ? error.response.data
                        : {}),

                    message:
                        UNKNOWN_MESSAGE,

                    status:
                        'unknown',

                    attemptId:
                        attempt.attemptId,

                    matchId:
                        attempt.matchId,

                    roundId:
                        attempt.roundId,
                },
            };

            /*
             * Optional automatic reconciliation.
             *
             * The caller can pass:
             *
             * verifyUnknownBet(details)
             */
            if (
                typeof options.verifyUnknownBet ===
                'function'
            ) {
                resolveBetActionSafety(
                    userId,
                    options.verifyUnknownBet,
                    {
                        attempt,
                        intervalMs:
                            options.resolveIntervalMs,
                        maxAttempts:
                            options.resolveAttempts,
                    }
                ).catch(() => {
                    /*
                     * Keep safety state if reconciliation
                     * itself fails.
                     */
                });
            }

            throw safeError;
        }

        /* -------------------------- DEFINITE FAILURE ---------------------- */

        const current =
            getBetActionSafety(userId);

        if (
            current?.attemptId ===
            attempt.attemptId
        ) {
            writeStoredValue(
                userId,
                null
            );
        }

        throw error;
    }
}

/* -------------------------------------------------------------------------- */
/* UNKNOWN reconciliation                                                     */
/* -------------------------------------------------------------------------- */

export async function resolveBetActionSafety(
    userId,
    verifier,
    options = {}
) {
    if (
        !userId ||
        typeof verifier !== 'function'
    ) {
        return {
            status: 'unknown',
        };
    }

    const maxAttempts =
        Number(
            options.maxAttempts ??
            DEFAULT_RESOLVE_ATTEMPTS
        );

    const intervalMs =
        Number(
            options.intervalMs ??
            DEFAULT_RESOLVE_INTERVAL_MS
        );

    for (
        let attemptNumber = 1;
        attemptNumber <= maxAttempts;
        attemptNumber += 1
    ) {
        const current =
            getBetActionSafety(userId);

        if (!current) {
            return {
                status: 'cleared',
            };
        }

        if (
            current.status !==
            'unknown'
        ) {
            return {
                status:
                    current.status,
            };
        }

        try {
            const result =
                await verifier({
                    attempt:
                        current,

                    attemptId:
                        current.attemptId,

                    userId,

                    matchId:
                        current.matchId,

                    roundId:
                        current.roundId,

                    gameId:
                        current.gameId,
                });

            /* -------------------------- FOUND ----------------------------- */

            if (
                result?.status ===
                    'confirmed' ||
                result?.found === true ||
                result?.exists === true
            ) {
                const latest =
                    getBetActionSafety(
                        userId
                    );

                if (
                    latest?.attemptId ===
                    current.attemptId
                ) {
                    writeStoredValue(
                        userId,
                        null
                    );
                }

                return {
                    status:
                        'confirmed',

                    data:
                        result,
                };
            }

            /* ------------------------ NOT FOUND --------------------------- */

            if (
                result?.status ===
                    'not_found' ||
                result?.found === false ||
                result?.exists === false
            ) {
                const latest =
                    getBetActionSafety(
                        userId
                    );

                if (
                    latest?.attemptId ===
                    current.attemptId
                ) {
                    writeStoredValue(
                        userId,
                        null
                    );
                }

                return {
                    status:
                        'not_found',

                    data:
                        result,
                };
            }
        } catch {
            /*
             * Verification failed.
             * Keep safety lock.
             */
        }

        const latest =
            getBetActionSafety(
                userId
            );

        if (
            !latest ||
            latest.attemptId !==
                current.attemptId
        ) {
            return {
                status:
                    'changed',
            };
        }

        if (
            attemptNumber <
            maxAttempts
        ) {
            await new Promise(
                (resolve) =>
                    setTimeout(
                        resolve,
                        intervalMs
                    )
            );
        }
    }

    return {
        status:
            'unknown',
    };
}

/* -------------------------------------------------------------------------- */
/* Manual confirmation                                                        */
/* -------------------------------------------------------------------------- */

export const confirmBetActionFromMyBets = (
    userId,
    attemptId = null
) => {
    if (!userId) {
        return false;
    }

    const current =
        getBetActionSafety(
            userId
        );

    if (!current) {
        return true;
    }

    if (
        attemptId &&
        current.attemptId !==
            attemptId
    ) {
        return false;
    }

    writeStoredValue(
        userId,
        null
    );

    return true;
};

export const rejectUnknownBetAction = (
    userId,
    attemptId = null
) => {
    if (!userId) {
        return false;
    }

    const current =
        getBetActionSafety(
            userId
        );

    if (!current) {
        return true;
    }

    if (
        attemptId &&
        current.attemptId !==
            attemptId
    ) {
        return false;
    }

    writeStoredValue(
        userId,
        null
    );

    return true;
};

/* -------------------------------------------------------------------------- */
/* Cleanup                                                                    */
/* -------------------------------------------------------------------------- */

export const cleanupBetActionSafety = (
    userId
) => {
    if (!userId) return;

    const snapshot =
        getBetActionSafety(
            userId
        );

    if (snapshot?.attemptId) {
        activeAttempts.delete(
            snapshot.attemptId
        );
    }

    clearExpiryTimer(
        userId
    );

    cachedSnapshots.delete(
        String(userId)
    );

    writeStoredValue(
        userId,
        null
    );
};